import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, FormGroup, Input, Label, Progress, Table } from "reactstrap";
import { toast } from "react-toastify";
import CustomModal from "../../../../share-components/modal";
import { startBulkDpdLabels, pollBulkDpdLabelStatus } from "../../store/orderSlice";

const POLL_INTERVAL_MS = 3000;

const emptyReport = () => ({
    labelled: [],
    regenerated: [],
    cannot_generate: [],
    non_dpd: [],
    not_found: [],
});

// The 'queued' state seeded by the create endpoint has report: [] (no run data yet);
// the job then overwrites it with the real object shape. Normalise both to one shape.
const normaliseReport = (report) => {
    if (!report || Array.isArray(report)) return emptyReport();
    return { ...emptyReport(), ...report };
};

const BulkDpdLabelsModal = ({ isOpen, closeDialog, initialOrderNumbers = [] }) => {
    const dispatch = useDispatch();

    const [orderNumbersText, setOrderNumbersText] = useState("");
    const [validationError, setValidationError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [runKey, setRunKey] = useState(null);
    const [runStatus, setRunStatus] = useState(null);
    const [pollError, setPollError] = useState(null);

    const pollIntervalRef = useRef(null);

    // Pre-fill from whatever orders were checked on the grid at the moment the modal was
    // opened. Deliberately keyed on isOpen only (not initialOrderNumbers) — this should run
    // once per open, not clobber the textarea if the caller's array reference changes while
    // the modal is already sitting open.
    useEffect(() => {
        if (isOpen) {
            setOrderNumbersText(initialOrderNumbers.length ? initialOrderNumbers.join("\n") : "");
            setValidationError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const clearPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    useEffect(() => {
        return () => clearPolling();
    }, []);

    const pollOnce = (key) => {
        dispatch(pollBulkDpdLabelStatus(key)).then((res) => {
            if (res.error) {
                clearPolling();
                setPollError(res.payload?.message || "Failed to check the run status.");
                return;
            }

            setRunStatus(res.payload);
            if (res.payload?.status === "completed" || res.payload?.status === "failed") {
                clearPolling();
            }
        });
    };

    const startPolling = (key) => {
        clearPolling();
        pollOnce(key);
        pollIntervalRef.current = setInterval(() => pollOnce(key), POLL_INTERVAL_MS);
    };

    const resetState = () => {
        clearPolling();
        setOrderNumbersText("");
        setValidationError(null);
        setSubmitting(false);
        setRunKey(null);
        setRunStatus(null);
        setPollError(null);
    };

    const handleClose = () => {
        resetState();
        closeDialog();
    };

    const handleSubmit = () => {
        if (submitting) return;

        if (!orderNumbersText.trim()) {
            setValidationError("Please paste at least one order number.");
            return;
        }

        setValidationError(null);
        setSubmitting(true);

        dispatch(startBulkDpdLabels(orderNumbersText)).then((res) => {
            setSubmitting(false);

            if (res.error) {
                toast.error(res.payload?.message || "Failed to start bulk DPD label generation.", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }

            const newRunKey = res.payload?.run_key;
            setRunKey(newRunKey);
            setRunStatus({ status: "queued", total: 0, completed: 0, failed: 0, report: [], result_url: null });
            startPolling(newRunKey);
        });
    };

    // WHY: not fetch(result_url) — this app's src/services/fack.backend.jsx globally
    // monkey-patches window.fetch and unconditionally reads opts.headers['Authorization'],
    // so any bare fetch(url) call throws "Cannot read properties of undefined (reading
    // 'headers')". result_url is already a plain public URL, so just navigate an anchor to
    // it — the same pattern smartReport/aiReport use for their job result file_url.
    const handleDownload = () => {
        if (!runStatus?.result_url) return;

        const link = document.createElement("a");
        link.href = runStatus.result_url;
        link.setAttribute("download", "");
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const report = normaliseReport(runStatus?.report);
    const hasLabels = report.labelled.length > 0 || report.regenerated.length > 0;
    const hasReportRows =
        report.labelled.length ||
        report.regenerated.length ||
        report.cannot_generate.length ||
        report.non_dpd.length ||
        report.not_found.length;

    return (
        <CustomModal
            isUseSelector
            size="lg"
            title="Bulk DPD International Labels"
            dialog={{ props: { isOpen } }}
            closeDialog={handleClose}
        >
            {!runKey && (
                <>
                    <FormGroup>
                        <Label>Order numbers</Label>
                        <Input
                            type="textarea"
                            rows={8}
                            placeholder={"Paste order numbers, separated by commas, spaces or new lines, e.g.\n#541515, #618444"}
                            value={orderNumbersText}
                            onChange={(e) => {
                                setOrderNumbersText(e.target.value);
                                setValidationError(null);
                            }}
                        />
                    </FormGroup>
                    {validationError && <div className="text-danger mb-2">{validationError}</div>}
                    <div className="text-right">
                        <Button color="primary" disabled={submitting} onClick={handleSubmit}>
                            {submitting && <i className="fa fa-spinner fa-spin mr-1" />}
                            Submit
                        </Button>
                    </div>
                </>
            )}

            {runKey && runStatus && (
                <div>
                    {pollError ? (
                        <div className="alert alert-warning">{pollError}</div>
                    ) : (
                        <>
                            {(runStatus.status === "queued" || runStatus.status === "processing") && (
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between mb-1">
                                        <b>
                                            <i className="fa fa-spinner fa-spin mr-1" />
                                            {runStatus.status === "queued" ? "Queued…" : "Processing…"}
                                        </b>
                                        <span>
                                            {runStatus.completed || 0} / {runStatus.total || 0}
                                            {runStatus.failed ? ` (${runStatus.failed} failed)` : ""}
                                        </span>
                                    </div>
                                    <Progress
                                        value={
                                            runStatus.total
                                                ? Math.round(((runStatus.completed || 0) / runStatus.total) * 100)
                                                : 0
                                        }
                                    />
                                </div>
                            )}

                            {runStatus.status === "failed" && (
                                <div className="alert alert-danger">
                                    {runStatus.error_message || "Bulk DPD label generation failed."}
                                </div>
                            )}

                            {runStatus.status === "completed" && (
                                <div className="mb-3">
                                    {hasLabels ? (
                                        <>
                                            <div className="alert alert-success">
                                                Done — {report.labelled.length + report.regenerated.length} label(s) merged
                                                {report.cannot_generate.length ? `, ${report.cannot_generate.length} skipped` : ""}.
                                            </div>
                                            <Button color="success" onClick={handleDownload}>
                                                <i className="fa fa-download mr-1" />
                                                Download merged PDF
                                            </Button>
                                        </>
                                    ) : (
                                        <div className="alert alert-info">No DPD labels found for the given order numbers.</div>
                                    )}
                                </div>
                            )}

                            {hasReportRows ? (
                                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                                    <Table size="sm" bordered responsive>
                                        <thead>
                                            <tr>
                                                <th>Order</th>
                                                <th>Result</th>
                                                <th>Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.labelled.map((row, i) => (
                                                <tr key={`labelled-${i}`}>
                                                    <td>{row.order}</td>
                                                    <td><span className="badge badge-success">Labelled</span></td>
                                                    <td>Label already existed (Invoice No: {row.invoice_no})</td>
                                                </tr>
                                            ))}
                                            {report.regenerated.map((row, i) => (
                                                <tr key={`regenerated-${i}`}>
                                                    <td>{row.order}</td>
                                                    <td><span className="badge badge-info">Regenerated</span></td>
                                                    <td>Label was missing and has been regenerated (Invoice No: {row.invoice_no})</td>
                                                </tr>
                                            ))}
                                            {report.cannot_generate.map((row, i) => (
                                                <tr key={`cannot-generate-${i}`}>
                                                    <td>{row.order}</td>
                                                    <td><span className="badge badge-danger">Cannot generate</span></td>
                                                    <td>{row.reason} (Invoice No: {row.invoice_no})</td>
                                                </tr>
                                            ))}
                                            {report.non_dpd.map((orderName, i) => (
                                                <tr key={`non-dpd-${i}`}>
                                                    <td>{orderName}</td>
                                                    <td><span className="badge badge-secondary">Not DPD</span></td>
                                                    <td>This order is not on the DPD delivery partner</td>
                                                </tr>
                                            ))}
                                            {report.not_found.map((token, i) => (
                                                <tr key={`not-found-${i}`}>
                                                    <td>{token}</td>
                                                    <td><span className="badge badge-warning">Not found</span></td>
                                                    <td>No matching order for this number</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : null}
                        </>
                    )}

                    <div className="text-right mt-3">
                        <Button color="secondary" onClick={handleClose}>Close</Button>
                    </div>
                </div>
            )}
        </CustomModal>
    );
};

export default BulkDpdLabelsModal;
